import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, FileText, AlertTriangle, Clock, Download, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface SiteRow { id: string; code: string | null; name_en: string | null; flag: string | null; health_score: number | null; trips_today: number }
interface LateRow { id: string; siteId: string; siteName: string | null; reportDate: string; entityType: string; description: string; daysLate: number; submittedAt: string; submittedByName: string | null }

interface WeeklyData {
  site: { id: string; code: string | null; name: string | null };
  weekStart: string; weekEnd: string;
  totals: { trips: number; deliveredQty: number; revenueSar: number; fuelSar: number; fuelLiters: number; expensesSar: number; maintenanceSar: number; cashVarianceSar: number; idleCostSar: number };
  perDay: { date: string; trips: number; revenueSar: number; fuelSar: number; flag: string | null }[];
  topAnomalyTypes: { anomalyType: string; count: number }[];
}

interface MonthlyData {
  site: { id: string; code: string | null; name: string | null };
  month: string; monthStart: string; monthEnd: string;
  totals: { trips: number; deliveredQty: number; revenueSar: number; fuelSar: number; fuelLiters: number; expensesSar: number; maintenanceSar: number; idleCostSar: number; cashVarianceSar: number; netProfitSar: number; avgTripsPerDay: number };
  drivers: { driverId: string; driverName: string | null; trips: number; revenueSar: number; fuelSar: number }[];
  vehicles: { vehicleId: string; plate: string | null; trips: number; revenueSar: number; utilizationPct: number | null }[];
  documentRenewals: number;
}

const FLAG_TONE: Record<string, string> = {
  green: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40',
  yellow: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40',
  red: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/40',
};

type Tab = 'daily' | 'weekly' | 'monthly' | 'late' | 'custom';

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function startOfWeek(d: Date): string {
  const day = d.getUTCDay(); // 0 = Sunday
  const sunday = new Date(d.getTime() - day * 86400000);
  return sunday.toISOString().slice(0, 10);
}

function thisMonth(): string {
  const d = new Date();
  return d.getUTCFullYear() + '-' + String(d.getUTCMonth() + 1).padStart(2, '0');
}

export default function ReportsHomePage() {
  const [tab, setTab] = useState<Tab>('daily');
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [late, setLate] = useState<LateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [siteId, setSiteId] = useState<string>('');
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [month, setMonth] = useState(thisMonth());
  const [weekly, setWeekly] = useState<WeeklyData | null>(null);
  const [monthly, setMonthly] = useState<MonthlyData | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'daily' || sites.length === 0) {
        const r = await apiClient.get<SiteRow[]>('/api/v1/daily-reports/sites');
        setSites(r.data);
        if (!siteId && r.data.length > 0) setSiteId(r.data[0].id);
      }
      if (tab === 'late') {
        const r = await apiClient.get<LateRow[]>('/api/v1/daily-reports/late-items?days=30');
        setLate(r.data);
      }
      if (tab === 'weekly' && siteId) {
        const r = await apiClient.get<WeeklyData>('/api/v1/reports/weekly?siteId=' + siteId + '&start=' + weekStart);
        setWeekly(r.data);
      }
      if (tab === 'monthly' && siteId) {
        const r = await apiClient.get<MonthlyData>('/api/v1/reports/monthly?siteId=' + siteId + '&month=' + month);
        setMonthly(r.data);
      }
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tab, siteId, weekStart, month, sites.length]);

  useEffect(() => { refresh(); }, [refresh]);

  async function downloadPdf(kind: 'weekly' | 'monthly') {
    const jwt = getStoredToken();
    const url = kind === 'weekly'
      ? '/api/v1/reports/weekly/pdf?siteId=' + siteId + '&start=' + weekStart
      : '/api/v1/reports/monthly/pdf?siteId=' + siteId + '&month=' + month;
    const res = await fetch(API_BASE + url, { headers: jwt ? { Authorization: 'Bearer ' + jwt } : {} });
    if (!res.ok) { window.alert('PDF failed: ' + res.statusText); return; }
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = objUrl;
    a.download = (kind === 'weekly' ? 'weekly_' : 'monthly_') + (kind === 'weekly' ? weekStart : month) + '.pdf';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><FileText className="h-4 w-4" /> Reports</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {([
            { v: 'daily',   l: 'Daily' },
            { v: 'weekly',  l: 'Weekly' },
            { v: 'monthly', l: 'Monthly' },
            { v: 'late',    l: 'Late items' },
            { v: 'custom',  l: 'Custom' },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-4 py-2 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>

        {tab !== 'daily' && tab !== 'late' && tab !== 'custom' && (
          <div className="rounded-lg border border-border p-3 flex flex-wrap items-end gap-3">
            <div className="space-y-1 flex-1 min-w-[16rem]">
              <Label htmlFor="site">Site</Label>
              <select id="site" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name_en ?? s.code ?? s.id.slice(0, 8)}</option>)}
              </select>
            </div>
            {tab === 'weekly' && (
              <div className="space-y-1">
                <Label htmlFor="ws">Week start (Sun)</Label>
                <Input id="ws" type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="w-44" />
              </div>
            )}
            {tab === 'monthly' && (
              <div className="space-y-1">
                <Label htmlFor="mo">Month</Label>
                <Input id="mo" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
              </div>
            )}
            <Button onClick={() => downloadPdf(tab as 'weekly' | 'monthly')} className="gap-2"><Download className="h-4 w-4" /> PDF</Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : tab === 'daily' ? (
          sites.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No active sites.</div>
          ) : (
            <div className="space-y-2">
              {sites.map((s) => (
                <Link key={s.id} to={'/dashboard/reports/sites/' + s.id + '/' + today}
                  className="block rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium">{s.name_en ?? s.code ?? s.id.slice(0, 8)}</div>
                    {s.flag && <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ' + FLAG_TONE[s.flag]}>{s.flag} · {s.health_score}/100</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Trips today: {s.trips_today}</div>
                </Link>
              ))}
            </div>
          )
        ) : tab === 'weekly' ? (
          weekly && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Stat label="Trips" value={String(weekly.totals.trips)} />
                <Stat label="Revenue" value={fmtSar(weekly.totals.revenueSar)} />
                <Stat label="Fuel" value={fmtSar(weekly.totals.fuelSar)} />
                <Stat label="Idle waste" value={fmtSar(weekly.totals.idleCostSar)} />
              </div>
              <div className="rounded-lg border border-border">
                <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">Per-day</div>
                {weekly.perDay.map((d) => (
                  <div key={d.date} className="px-4 py-2 border-t border-border flex items-baseline justify-between text-sm">
                    <span className="font-mono">{d.date}</span>
                    <span className="font-mono text-xs text-muted-foreground">trips {d.trips} · {fmtSar(d.revenueSar)} · fuel {fmtSar(d.fuelSar)}</span>
                    {d.flag && <span className={'inline-flex text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + FLAG_TONE[d.flag]}>{d.flag}</span>}
                  </div>
                ))}
              </div>
              {weekly.topAnomalyTypes.length > 0 && (
                <div className="rounded-lg border border-border">
                  <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">Top anomalies</div>
                  {weekly.topAnomalyTypes.map((a) => (
                    <div key={a.anomalyType} className="px-4 py-2 border-t border-border flex items-baseline justify-between text-sm">
                      <span>{a.anomalyType}</span>
                      <span className="font-mono">{a.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        ) : tab === 'monthly' ? (
          monthly && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <Stat label="Trips" value={String(monthly.totals.trips)} sub={'avg ' + monthly.totals.avgTripsPerDay + '/day'} />
                <Stat label="Revenue" value={fmtSar(monthly.totals.revenueSar)} />
                <Stat label="Net profit" value={fmtSar(monthly.totals.netProfitSar)} tone={monthly.totals.netProfitSar >= 0 ? 'good' : 'bad'} />
                <Stat label="Fuel" value={fmtSar(monthly.totals.fuelSar)} />
                <Stat label="Maintenance" value={fmtSar(monthly.totals.maintenanceSar)} />
                <Stat label="Idle waste" value={fmtSar(monthly.totals.idleCostSar)} />
              </div>
              {monthly.drivers.length > 0 && (
                <div className="rounded-lg border border-border">
                  <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">Driver leaderboard</div>
                  {monthly.drivers.map((d) => (
                    <div key={d.driverId} className="px-4 py-2 border-t border-border flex items-baseline justify-between text-sm">
                      <span>{d.driverName ?? d.driverId.slice(0, 8)}</span>
                      <span className="text-xs text-muted-foreground">trips {d.trips} · {fmtSar(d.revenueSar)} revenue · {fmtSar(d.fuelSar)} fuel</span>
                    </div>
                  ))}
                </div>
              )}
              {monthly.vehicles.length > 0 && (
                <div className="rounded-lg border border-border">
                  <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">Vehicle utilization</div>
                  {monthly.vehicles.map((v) => (
                    <div key={v.vehicleId} className="px-4 py-2 border-t border-border flex items-baseline justify-between text-sm">
                      <span className="font-mono">{v.plate ?? v.vehicleId.slice(0, 8)}</span>
                      <span className="text-xs text-muted-foreground">trips {v.trips} · {fmtSar(v.revenueSar)} · util {v.utilizationPct ?? 0}%</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-sm text-muted-foreground">Document renewals processed this month: {monthly.documentRenewals}</div>
            </div>
          )
        ) : tab === 'late' ? (
          late.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No late items in the last 30 days.</div>
          ) : (
            <div className="space-y-1">
              {late.map((l) => (
                <Link key={l.id} to={'/dashboard/reports/sites/' + l.siteId + '/' + l.reportDate}
                  className="block rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm">{l.description}</div>
                    <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (l.daysLate > 3 ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400' : 'bg-amber-500/15 text-amber-700 dark:text-amber-400')}>
                      <Clock className="h-3 w-3 me-1" /> +{l.daysLate}d
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {l.siteName} · report {l.reportDate} · {l.entityType} · submitted {new Date(l.submittedAt).toLocaleDateString('en-GB')}
                  </div>
                </Link>
              ))}
            </div>
          )
        ) : (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground inline-flex flex-col items-center gap-2 mx-auto">
            <CalendarDays className="h-8 w-8 opacity-40" />
            Custom report builder is shipping in PR R5. For now, use Daily / Weekly / Monthly tabs above.
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' }) {
  const cls = tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'bad' ? 'text-rose-600 dark:text-rose-400' : '';
  return (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-lg font-medium mt-0.5 ' + cls}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
